import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AdminLayout from '../components/AdminLayout'
import { AdminTable, Column } from '../components/AdminTable'
import { CalendarDays } from 'lucide-react'
import adminService from '../utils/adminService'
import { ApiError } from '../utils/api'

type FilingRow = {
  key: string
  type: string
  period: string
  filingDate: string
  submittedBy: string
  dueDate: string
  taxPayable: string
  docs: string
  status: 'Submitted' | 'Draft' | 'New'
}

export default function AdminCompanyDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'filings' | 'deadlines'>('filings')
  const [company, setCompany] = useState<any | null>(null)
  const [allFilings, setAllFilings] = useState<FilingRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10
  
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const filings = allFilings.slice(startIndex, endIndex)

  const columns: Column<FilingRow>[] = [
    { key: 'type', label: 'Type' },
    { key: 'period', label: 'Period' },
    { key: 'filingDate', label: 'Filing Date' },
    { key: 'submittedBy', label: 'Submitted By' },
    { key: 'dueDate', label: 'Due Date' },
    { key: 'taxPayable', label: 'Tax Payable' },
    { key: 'docs', label: 'Docs', render: row => <span className='text-gray-500'>{row.docs}</span> },
    {
      key: 'status',
      label: 'Status',
      render: row => {
        const style =
          row.status === 'Submitted'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
            : row.status === 'Draft'
            ? 'bg-amber-50 text-amber-700 border-amber-100'
            : 'bg-gray-100 text-gray-700 border-gray-200'
        return (
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${style}`}>
            {row.status}
          </span>
        )
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: row => (
        <button
          onClick={() => navigate(`/admin/filings/${row.key}`)}
          className='inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50'
        >
          View
        </button>
      ),
    },
  ]

  useEffect(() => {
    const loadCompany = async () => {
      if (!id) return
      setError(null)
      setLoading(true)
      try {
        const { response } = await adminService.getCompany(id)
        const data: any = (response as any)?.data || response
        setCompany(data?.company || data)

        const filingsData = data?.filings || []
        setAllFilings(
          filingsData.map((item: any, idx: number) => ({
            key: item.id || item._id || `${idx}`,
            type: item.type || 'CT600',
            period: item.period || item.accountingPeriod || '—',
            filingDate: item.filingDate
              ? new Date(item.filingDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              : '—',
            submittedBy: item.submittedBy?.name || item.submittedBy?.email || '—',
            dueDate: item.dueDate
              ? new Date(item.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              : '—',
            taxPayable: item.taxPayable ? `€${Number(item.taxPayable).toLocaleString()}` : '--',
            docs: item.docs ? String(item.docs) : '—',
            status: (item.status || 'submitted') as FilingRow['status'],
          }))
        )
      } catch (err) {
        console.error(err)
        setError(err instanceof ApiError ? err.message : 'Unable to load company details.')
      } finally {
        setLoading(false)
      }
    }

    void loadCompany()
  }, [id])

  return (
    <AdminLayout>
      <div className='px-4 py-4 sm:px-6 sm:py-6 lg:px-10 space-y-5 sm:space-y-6'>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h1 className='text-xl sm:text-2xl font-bold text-gray-900'>Hi, Emmanuel</h1>
            <p className='text-sm text-gray-500'>You are welcome</p>
          </div>
          <div className='inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs sm:text-sm text-gray-700 shadow-sm self-start sm:self-auto'>
            <CalendarDays className='h-4 w-4 text-gray-500 flex-shrink-0' />
            <span className='whitespace-nowrap'>15 Jun - 21 Jun 2025</span>
          </div>
        </div>

        <h2 className='text-lg sm:text-xl font-bold text-gray-900'>Company Management</h2>

          <div className='rounded-2xl bg-white border border-gray-100 shadow-sm p-4 sm:p-5 space-y-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2 min-w-0'>
              <button
                onClick={() => window.history.back()}
                className='h-8 w-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-700 hover:text-gray-900 flex-shrink-0'
              >
                ←
              </button>
              <p className='text-base sm:text-lg font-semibold text-gray-900 truncate'>{company?.company?.name || company?.name || 'Company'}</p>
            </div>
          </div>

          <div className='grid grid-cols-1 gap-4 lg:grid-cols-[320px,1fr]'>
            <div className='rounded-2xl bg-gray-50 border border-gray-200 p-4 sm:p-5 space-y-2 text-sm text-gray-800 break-words'>
              {loading && !company ? (
                <div className='space-y-3 animate-pulse'>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className='h-3 rounded bg-gray-200' style={{ width: `${50 + (i % 4) * 12}%` }} />
                  ))}
                </div>
              ) : (
                <>
              <p><span className='font-semibold'>Registered By:</span> {company?.company?.createdBy?.name || '—'}</p>
              <p><span className='font-semibold'>Company Owner:</span> {company?.companyOwner?.name || '—'}</p>
              <p><span className='font-semibold'>Company Name:</span> {company?.company?.name || company?.name || '—'}</p>
              <p><span className='font-semibold'>Company No:</span> {company?.company?.companyNumber || '—'}</p>
              <p>
                <span className='font-semibold'>Status:</span>{' '}
                <span className='inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 border border-emerald-100'>
                  {company?.companyStatus || company?.company?.companyStatus || 'Active'}
                </span>
              </p>
              <p><span className='font-semibold'>Address:</span> {company?.company?.registration?.registeredOffice || '—'}</p>
              <p><span className='font-semibold'>Incorporation:</span> {company?.company?.registration?.incorporationDate
                ? new Date(company.company.registration.incorporationDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                : '—'}</p>
              <p><span className='font-semibold'>Officer Currently Active:</span> John Doe <span className='text-red-500 cursor-pointer'>View All</span></p>
              <p><span className='font-semibold'>Company Type:</span> {company?.companyType || company?.company?.companyType || '—'}</p>
              <p><span className='font-semibold'>Accs Category:</span> {company?.accsCategory || 'Total Exemption Full'}</p>
              <p><span className='font-semibold'>Accs Ref Date (ARD):</span> {company?.accsRefDate || '—'}</p>
              <p><span className='font-semibold'>Accs Last Made (ARD):</span> {company?.accsLastMade || '—'}</p>
              <p><span className='font-semibold'>Accs Due Date:</span> {company?.accsDueDate || '—'}</p>
              <button className='mt-4 w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50'>
                Deactivate
              </button>
                </>
              )}
            </div>

            <div className='space-y-4 min-w-0'>
              <div className='flex flex-wrap items-center gap-2'>
                <button
                  onClick={() => setActiveTab('filings')}
                  className={`rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold ${
                    activeTab === 'filings' ? 'bg-[#0b1f3a] text-white' : 'bg-white border border-gray-200 text-gray-800'
                  }`}
                >
                  Filing History
                </button>
                <button
                  onClick={() => setActiveTab('deadlines')}
                  className={`rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold ${
                    activeTab === 'deadlines' ? 'bg-[#0b1f3a] text-white' : 'bg-white border border-gray-200 text-gray-800'
                  }`}
                >
                  Deadlines
                </button>
              </div>

              {error && (
                <div className='rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700'>
                  {error}
                </div>
              )}

              {activeTab === 'filings' && (
                <AdminTable<FilingRow>
                  columns={columns}
                  data={filings}
                  loading={loading}
                  emptyMessage='No filings found for this company.'
                  searchPlaceholder='Search Company'
                  actions={null}
                  currentPage={currentPage}
                  totalItems={allFilings.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                />
              )}
              {activeTab === 'deadlines' && (
                <div className='rounded-2xl border border-gray-100 bg-gray-50 p-6 text-sm text-gray-700'>
                  No deadlines available.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
